import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Home, Ticket, Sparkles, MessageCircle, User, LogOut, Music, Menu, X, Shield, Plus, UserPlus } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useState } from 'react';

export default function MainLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const navItems = [
    { to: '/discover', icon: Home, label: 'Discover' },
    { to: '/vibe', icon: Sparkles, label: 'Vibe' },
    { to: '/chat', icon: MessageCircle, label: 'Chat' },
    { to: '/tickets', icon: Ticket, label: 'Tickets' },
    { to: '/profile', icon: User, label: 'Profile' },
  ];

  // Add host navigation if user is a host
  const hostNavItems = user?.role === 'host' || user?.role === 'admin'
    ? [{ to: '/manage-events', label: 'Manage Events' }, { to: '/create-event', label: 'Create Event' }]
    : [];

  // Add artist navigation if user is an artist
  const artistNavItems = user?.role === 'artist' || user?.role === 'admin'
    ? [{ to: '/artist-dashboard', label: 'Artist Dashboard', icon: Music }]
    : [];

  // Add admin navigation if user is admin
  const adminNavItems = user?.role === 'admin'
    ? [{ to: '/admin', label: 'Admin Dashboard' }]
    : [];

  // Items for "Become" section (for regular users)
  const becomeItems = user?.role === 'user'
    ? [
        { to: '/become-host', label: 'Become a Host' },
        { to: '/become-artist', label: 'Become an Artist' }
      ]
    : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background-paper/95 backdrop-blur supports-[backdrop-filter]:bg-background-paper/60">
        <div className="container mx-auto flex h-16 items-center px-4">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_3df344ce-b978-4374-b0b0-7e9d1746e70f/artifacts/w7gh4arh_vibezlink_logo_transparent.png" 
              alt="Vibezlink" 
              className="h-10 w-auto"
            />
            <h1 className="text-xl font-heading font-bold bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent hidden sm:block">
              VIBEZLINK
            </h1>
          </div>

          <div className="ml-auto flex items-center space-x-4">
            <span className="text-sm text-muted-foreground hidden md:inline">
              {user?.name}
            </span>
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-muted-foreground hover:text-foreground"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Slide-out Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed right-0 top-16 bottom-0 w-64 glass border-l border-white/10 p-4 overflow-y-auto">
            <div className="space-y-2">
              {user?.role === 'admin' && (
                <>
                  <p className="text-xs text-gray-500 uppercase px-4 mb-2">Admin</p>
                  <NavLink
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                        isActive ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-white/5'
                      }`
                    }
                  >
                    <Shield className="h-5 w-5" />
                    <span>Admin Dashboard</span>
                  </NavLink>
                </>
              )}
              
              {(user?.role === 'host' || user?.role === 'admin') && (
                <>
                  <p className="text-xs text-gray-500 uppercase px-4 mb-2 mt-4">Host</p>
                  <NavLink
                    to="/create-event"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                        isActive ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-white/5'
                      }`
                    }
                  >
                    <Plus className="h-5 w-5" />
                    <span>Create Event</span>
                  </NavLink>
                  <NavLink
                    to="/manage-events"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                        isActive ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-white/5'
                      }`
                    }
                  >
                    <Ticket className="h-5 w-5" />
                    <span>Manage Events</span>
                  </NavLink>
                </>
              )}

              {(user?.role === 'artist' || user?.role === 'admin') && (
                <>
                  <p className="text-xs text-gray-500 uppercase px-4 mb-2 mt-4">Artist</p>
                  <NavLink
                    to="/artist-dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                        isActive ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-white/5'
                      }`
                    }
                  >
                    <Music className="h-5 w-5" />
                    <span>Artist Dashboard</span>
                  </NavLink>
                </>
              )}

              {user?.role === 'user' && (
                <>
                  <p className="text-xs text-gray-500 uppercase px-4 mb-2 mt-4">Opportunities</p>
                  <NavLink
                    to="/become-host"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                        isActive ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-white/5'
                      }`
                    }
                  >
                    <UserPlus className="h-5 w-5" />
                    <span>Become a Host</span>
                  </NavLink>
                  <NavLink
                    to="/become-artist"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                        isActive ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-white/5'
                      }`
                    }
                  >
                    <Music className="h-5 w-5" />
                    <span>Become an Artist</span>
                  </NavLink>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="mx-auto px-6 py-6 md:ml-56 md:px-8">
        <Outlet />
      </main>

      {/* Bottom Navigation (Mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden glass border-t border-white/10">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              <span className="text-xs mt-1">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Desktop Side Navigation */}
      <nav className="hidden md:block fixed left-0 top-16 bottom-0 w-56 glass border-r border-white/10 p-4 overflow-y-auto z-40">
        <div className="space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-primary text-white shadow-glow'
                    : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          
          {hostNavItems.length > 0 && (
            <>
              <div className="h-px bg-white/10 my-4" />
              <p className="text-xs text-gray-500 uppercase px-4 mb-2">Host</p>
              {hostNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-primary text-white shadow-glow'
                        : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                    }`
                  }
                >
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}

          {artistNavItems.length > 0 && (
            <>
              <div className="h-px bg-white/10 my-4" />
              <p className="text-xs text-gray-500 uppercase px-4 mb-2">Artist</p>
              {artistNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-primary text-white shadow-glow'
                        : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                    }`
                  }
                >
                  {item.icon && <item.icon className="h-5 w-5" />}
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}

          {adminNavItems.length > 0 && (
            <>
              <div className="h-px bg-white/10 my-4" />
              <p className="text-xs text-gray-500 uppercase px-4 mb-2">Admin</p>
              {adminNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-primary text-white shadow-glow'
                        : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                    }`
                  }
                >
                  <Shield className="h-5 w-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}

          {becomeItems.length > 0 && (
            <>
              <div className="h-px bg-white/10 my-4" />
              <p className="text-xs text-gray-500 uppercase px-4 mb-2">Opportunities</p>
              {becomeItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-primary text-white shadow-glow'
                        : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                    }`
                  }
                >
                  <UserPlus className="h-5 w-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </div>
      </nav>

      {/* Add styles */}
      <style>{`
        @media (max-width: 767px) {
          main {
            margin-left: 0;
            padding-left: 1rem;
            padding-right: 1rem;
          }
        }
      `}</style>
    </div>
  );
}
